import { ArrowRight, ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

const INSIGHT_TONES = {
  comfortable: {
    label: 'Comfortable',
    panel: 'border-primary/30 bg-primary/5',
    text: 'text-primary',
    badge: 'border-primary/20 bg-primary/10 text-primary',
  },
  achievable: {
    label: 'Achievable',
    panel: 'border-chart-4/30 bg-chart-4/10',
    text: 'text-chart-4',
    badge: 'border-chart-4/20 bg-chart-4/10 text-chart-4',
  },
  stretch: {
    label: 'Stretch',
    panel: 'border-chart-4/30 bg-chart-4/10',
    text: 'text-chart-4',
    badge: 'border-chart-4/20 bg-chart-4/10 text-chart-4',
  },
  aggressive: {
    label: 'Aggressive',
    panel: 'border-destructive/30 bg-destructive/10',
    text: 'text-destructive',
    badge: 'border-destructive/20 bg-destructive/10 text-destructive',
  },
  extreme: {
    label: 'Extreme',
    panel: 'border-destructive/30 bg-destructive/10',
    text: 'text-destructive',
    badge: 'border-destructive/20 bg-destructive/10 text-destructive',
  },
}

function insightToneFor(feasibilityBand) {
  return INSIGHT_TONES[feasibilityBand] || INSIGHT_TONES.achievable
}

function InsightReasoning({ insight }) {
  const unlockItems = Array.isArray(insight.unlock) ? insight.unlock : null

  return (
    <div className='mt-4 grid gap-4 text-sm text-muted-foreground sm:grid-cols-3'>
      <ReasoningColumn title='What Sentinel sees'>{insight.what}</ReasoningColumn>
      <ReasoningColumn title='Why this matters'>{insight.why}</ReasoningColumn>
      <ReasoningColumn title='Likely unlock'>
        {unlockItems ? (
          <ul className='list-disc space-y-1 pl-4'>
            {unlockItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        ) : insight.unlock}
      </ReasoningColumn>
    </div>
  )
}

function ReasoningColumn({ title, children }) {
  return (
    <div>
      <p className='text-xs font-semibold uppercase tracking-widest text-foreground'>{title}</p>
      <div className='mt-1'>{children}</div>
    </div>
  )
}

export function TargetTransitionBridge({ bridge, open, onOpenChange, onConfirm, isSubmitting = false }) {
  if (!bridge) return null

  const [primaryInsight, ...secondaryInsights] = bridge.insights
  const primaryInsightTone = insightToneFor(bridge.feasibilityBand)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-2xl'>
        <DialogHeader>
          <DialogTitle>Sentinel View</DialogTitle>
          <DialogDescription>
            Sentinel has reviewed your goals, resources, and timeframe before strategy selection.
          </DialogDescription>
        </DialogHeader>

        <section aria-labelledby='target-snapshot-title' className='space-y-3'>
          <p
            id='target-snapshot-title'
            className='text-xs font-semibold uppercase tracking-widest text-muted-foreground'
          >
            Target Snapshot
          </p>
          <dl className='grid gap-3 rounded-lg border border-border bg-muted/30 p-4 sm:grid-cols-4'>
            {bridge.targetSummary.map((item) => (
              <div key={item.label}>
                <dt className='text-xs font-semibold uppercase tracking-widest text-muted-foreground'>{item.label}</dt>
                <dd className='mt-1 text-sm font-semibold text-foreground'>{item.value}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section
          aria-labelledby='system-insight-title'
          className={cn('rounded-lg border p-5', primaryInsightTone.panel)}
        >
          <div className='flex flex-wrap items-center justify-between gap-3'>
            <p
              id='system-insight-title'
              className={cn('text-xs font-bold uppercase tracking-widest', primaryInsightTone.text)}
            >
              SYSTEM INSIGHT
            </p>
            <span
              className={cn(
                'rounded-md border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest',
                primaryInsightTone.badge,
              )}
            >
              {primaryInsightTone.label}
            </span>
          </div>
          <h3 className='mt-2 text-lg font-bold leading-snug text-foreground'>{primaryInsight.title}</h3>
          <details className='group mt-3'>
            <summary
              className={cn(
                'flex cursor-pointer list-none items-center gap-2 text-sm font-semibold',
                primaryInsightTone.text,
              )}
            >
              <span>See deeper reasoning</span>
              <ChevronDown
                aria-hidden='true'
                className='h-4 w-4 transition-transform group-open:rotate-180'
              />
            </summary>
            <InsightReasoning insight={primaryInsight} />
          </details>
        </section>

        {secondaryInsights.length > 0 ? (
          <div className='space-y-3'>
            {secondaryInsights.map((insight) => (
              <details key={insight.title} className='group rounded-lg border border-border bg-background p-4'>
                <summary className='flex cursor-pointer list-none items-start justify-between gap-3 text-sm font-bold text-foreground'>
                  <span>{insight.title}</span>
                  <span className='flex shrink-0 items-center gap-1 text-xs font-semibold text-primary'>
                    See deeper reasoning
                    <ChevronDown
                      aria-hidden='true'
                      className='h-4 w-4 transition-transform group-open:rotate-180'
                    />
                  </span>
                </summary>
                <InsightReasoning insight={insight} />
              </details>
            ))}
          </div>
        ) : null}

        <section className='rounded-lg border border-border bg-muted/30 p-4'>
          <p className='text-sm font-bold text-foreground'>
            Based on your goals, let&apos;s identify the right strategy.
          </p>
          <p className='mt-2 text-sm text-muted-foreground'>Sentinel will recommend strategies aligned with:</p>
          <ul className='mt-3 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2'>
            {[
              'Your income goals',
              'Your capital base',
              'Your preferred involvement level',
              'Your growth timeline',
            ].map((item) => (
              <li key={item} className='flex gap-2'>
                <span aria-hidden='true' className='mt-2 h-1.5 w-1.5 rounded-full bg-primary' />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        <DialogFooter>
          <Button type='button' variant='outline' onClick={() => onOpenChange?.(false)}>
            Edit Targets
          </Button>
          <Button type='button' disabled={isSubmitting} onClick={onConfirm}>
            Continue to Strategy Selection
            <ArrowRight aria-hidden='true' className='h-4 w-4' />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
